import React, { useState, useRef, useEffect } from "react";
import ColorInfo from "./ColorInfo";

const PixelColorViewer = (props) => {
    const {
        titleClose,
        titleCopy,
        titleCopied,
        titleCurrentValue,
        titleSelectedValue,
        tip1,
    } = props.attributes;

    const [pixelColor, setPixelColor] = useState({
        rgba: "",
        hex: "",
        hsl: "",
        cmyk: "",
        hsv: "",
        lab: "",
        name: "",
        rgbaA: "",
        hsla: "",
        xyz: "",
        lch: "",
    });
    const [selectedColor, setSelectedColor] = useState({
        rgba: "",
        hex: "",
        hsl: "",
        cmyk: "",
        hsv: "",
        lab: "",
        /* name: '', */
        rgbaA: "",
        hsla: "",
        xyz: "",
        lch: "",
    });
    const canvasRef = useRef(null);
    const selectedRef = useRef(null);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth * 0.9,
        height: window.innerHeight,
    });
    const [isCursorInsideCanvas, setIsCursorInsideCanvas] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [colorData, setColorData] = useState(null);
    const [neighboringColors, setNeighboringColors] = useState([]);
    const [maxHeight, setMaxHeight] = useState("400px"); // Значение по умолчанию для десктопа

    useEffect(() => {
        const handleResizePopup = () => {
            const screenWidth = window.innerWidth;
            if (screenWidth <= 768) {
                setMaxHeight("200px"); // Мобильные устройства
            } else if (screenWidth <= 1024) {
                setMaxHeight("250px"); // Планшеты
            } else {
                setMaxHeight("280px"); // Десктопы
            }
        };

        // Вызываем функцию при загрузке и изменении размера окна
        handleResizePopup();
        window.addEventListener("resize", handleResizePopup);

        // Очищаем слушатель при размонтировании компонента
        return () => {
            window.removeEventListener("resize", handleResizePopup);
        };
    }, []);

    const handleMouseLeave = () => {
        setIsCursorInsideCanvas(false);
    };

    const handleMouseEnter = () => {
        setIsCursorInsideCanvas(true);
    };

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth * 0.9,
                height: window.innerHeight,
            });
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const image = new Image();
        image.src = props.imageUrl;

        const drawImageScaled = () => {
            const scaleFactor = Math.min(
                canvas.width / image.width,
                canvas.height / image.height
            );
            const imageWidth = image.width * scaleFactor;
            const imageHeight = image.height * scaleFactor;
            ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear the canvas
            ctx.drawImage(image, 0, 0, imageWidth, imageHeight);
        };

        if (image.complete) {
            // If the image is already loaded (e.g., from the cache), draw it immediately
            drawImageScaled();
        } else {
            // Otherwise, draw the image once it's fully loaded
            image.onload = drawImageScaled;
        }
    }, [props.imageUrl, windowSize]);

    const drawCheckerboard = (context, width, height) => {
        const squareSize = 20; // Размер клеток сетки
        context.fillStyle = "rgba(200, 200, 200, 1)";
        for (let x = 0; x < width; x += squareSize) {
            for (let y = 0; y < height; y += squareSize) {
                if ((x / squareSize) % 2 === (y / squareSize) % 2) {
                    context.fillRect(x, y, squareSize, squareSize);
                }
            }
        }
    };

    const handleMouseMove = (event) => {
        const { offsetX, offsetY } = event.nativeEvent;
        const pixelData = getPixelData(offsetX, offsetY);
        const neighboringPixels = getNeighboringPixels({ offsetX, offsetY });
        setPixelColor(pixelData);
        setNeighboringColors(neighboringPixels);
    };

    const handleClick = (event) => {
        const { offsetX, offsetY } = event.nativeEvent;
        const pixelData = getPixelData(offsetX, offsetY);
        setSelectedColor(pixelData);
        if (selectedRef.current) {
            /* selectedRef.current.scrollIntoView({ behavior: 'smooth' }); */
            const element = selectedRef.current;
            const scrollPosition = element.offsetTop - 120;
            /* window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
            }); */
        }
        setModalVisible(true);
    };

    const getPixelData = (x, y) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        canvas.willReadFrequently = true;
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixelIndex = (y * imageData.width + x) * 4;

        const r = imageData.data[pixelIndex];
        const g = imageData.data[pixelIndex + 1];
        const b = imageData.data[pixelIndex + 2];
        const a = imageData.data[pixelIndex + 3] / 255;

        const rgba = `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
        const hex = rgbToHex(r, g, b);
        const hsl = rgbToHsl(r, g, b);
        const cmyk = rgbToCmyk(r, g, b);
        const hsv = rgbToHsv(r, g, b);
        const lab = rgbToLab(r, g, b);
        const name = getNamedColor(hex);
        const rgbaA = `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
        const hsla = `hsla(${hsl[0].toFixed(2)}, ${hsl[1].toFixed(
            2
        )}%, ${hsl[2].toFixed(2)}%, ${a.toFixed(2)})`;
        const xyz = rgbToXyz(r, g, b);
        const lch = rgbToLch(r, g, b);

        return {
            rgba,
            hex,
            hsl,
            cmyk,
            hsv,
            lab,
            name,
            rgbaA,
            hsla,
            xyz,
            lch,
        };
    };

    const rgbToHex = (r, g, b) => {
        const componentToHex = (c) => {
            const hex = c.toString(16);
            return hex.length === 1 ? "0" + hex : hex;
        };
        return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
    };

    const rgbToHsl = (r, g, b) => {
        const normalize = (value, max) => value / max;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const delta = max - min;

        let h, s, l;

        if (delta === 0) {
            h = 0;
        } else if (max === r) {
            h = ((g - b) / delta) % 6;
        } else if (max === g) {
            h = (b - r) / delta + 2;
        } else {
            h = (r - g) / delta + 4;
        }

        h = Math.round(h * 60);
        if (h < 0) {
            h += 360;
        }

        l = (max + min) / 2;

        if (delta === 0) {
            s = 0;
        } else {
            s = delta / (1 - Math.abs(2 * l - 1));
        }

        s = Math.round(s * 100);
        l = Math.round(l * 100);

        return [h, s, l];
    };

    const rgbToCmyk = (r, g, b) => {
        const normalize = (value, max) => value / max;

        const c = 1 - normalize(r, 255);
        const m = 1 - normalize(g, 255);
        const y = 1 - normalize(b, 255);

        const k = Math.min(c, m, y);
        const cmyk = [
            Math.round(((c - k) / (1 - k)) * 100),
            Math.round(((m - k) / (1 - k)) * 100),
            Math.round(((y - k) / (1 - k)) * 100),
            Math.round(k * 100),
        ];

        return cmyk;
    };

    const rgbToHsv = (r, g, b) => {
        const normalize = (value, max) => value / max;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const delta = max - min;

        let h, s, v;

        if (delta === 0) {
            h = 0;
        } else if (max === r) {
            h = ((g - b) / delta) % 6;
        } else if (max === g) {
            h = (b - r) / delta + 2;
        } else {
            h = (r - g) / delta + 4;
        }

        h = Math.round(h * 60);
        if (h < 0) {
            h += 360;
        }

        v = normalize(max, 255) * 100;
        s = max === 0 ? 0 : (delta / max) * 100;

        s = Math.round(s);
        v = Math.round(v);

        return [h, s, v];
    };

    const rgbToLab = (red, green, blue) => {
        const normalize = (value, max) => value / max;

        const x =
            normalize(red, 255) * 0.4124564 +
            normalize(green, 255) * 0.3575761 +
            normalize(blue, 255) * 0.1804375;
        const y =
            normalize(red, 255) * 0.2126729 +
            normalize(green, 255) * 0.7151522 +
            normalize(blue, 255) * 0.072175;
        const z =
            normalize(red, 255) * 0.0193339 +
            normalize(green, 255) * 0.119192 +
            normalize(blue, 255) * 0.9503041;

        const epsilon = 0.008856;
        const kappa = 903.3;

        const f = (t) => (t > epsilon ? Math.cbrt(t) : (kappa * t + 16) / 116);

        const fy = f(y / 100);
        const l = fy > epsilon ? 116 * Math.cbrt(fy) - 16 : kappa * fy;

        const fx = f(x / 95.047);
        const fz = f(z / 108.883);

        const a = 500 * (fx - fy);
        const bValue = 200 * (fy - fz);

        return [l, a, bValue];
    };

    const rgbToXyz = (r, g, b) => {
        const normalize = (value, max) => value / max;

        const x =
            normalize(r, 255) * 0.4124564 +
            normalize(g, 255) * 0.3575761 +
            normalize(b, 255) * 0.1804375;
        const y =
            normalize(r, 255) * 0.2126729 +
            normalize(g, 255) * 0.7151522 +
            normalize(b, 255) * 0.072175;
        const z =
            normalize(r, 255) * 0.0193339 +
            normalize(g, 255) * 0.119192 +
            normalize(b, 255) * 0.9503041;

        return [x, y, z];
    };

    const rgbToLch = (r, g, b) => {
        const lab = rgbToLab(r, g, b);

        const l = lab[0];
        const c = Math.sqrt(Math.pow(lab[1], 2) + Math.pow(lab[2], 2));
        let h = Math.atan2(lab[2], lab[1]) * (180 / Math.PI);

        if (h < 0) {
            h += 360;
        }

        return [l, c, h];
    };

    const getNamedColor = (hex) => {
        const namedColors = {
            "#5d8aa8": "Rackley",
            "#f0f8ff": "Alice blue",
            "#e32636": "Rose madder",
            "#efdecd": "Almond",
            "#e52b50": "Amaranth",
            "#ffbf00": "Fluorescent orange",
            "#ff033e": "American rose",
            "#9966cc": "Amethyst",
            "#a4c639": "Android Green",
            "#f2f3f4": "Munsell",
            "#cd9575": "Antique brass",
            "#915c83": "Antique fuchsia",
            "#faebd7": "Moccasin",
            "#008000": "Office green",
            "#8db600": "Apple green",
            "#fbceb1": "Apricot",
            "#00ffff": "Waterspout",
            "#7fffd4": "Aquamarine",
            "#4b5320": "Army green",
            "#e9d66b": "Hansa yellow",
            "#b2beb5": "Ash grey",
            "#87a96b": "Asparagus",
            "#ff9966": "Atomic tangerine",
            "#a52a2a": "Red brown",
            "#fdee00": "Aureolin",
            "#6e7f80": "AuroMetalSaurus",
            "#ff2052": "Awesome",
            "#007fff": "Azure",
            "#f0ffff": "Azure mist/web",
            "#89cff0": "Baby blue",
            "#a1caf1": "Baby blue eyes",
            "#f4c2c2": "Tea rose",
            "#21abcd": "Ball Blue",
            "#fae7b5": "Banana Mania",
            "#ffe135": "Banana yellow",
            "#848482": "Battleship grey",
            "#98777b": "Bazaar",
            "#bcd4e6": "Pale aqua",
            "#9f8170": "Beaver",
            "#f5f5dc": "Beige",
            "#ffe4c4": "Bisque",
            "#3d2b1f": "Bistre",
            "#fe6f5e": "Bittersweet",
            "#000000": "Black",
            "#ffebcd": "Blanched Almond",
            "#318ce7": "Bleu de France",
            "#ace5ee": "Blizzard Blue",
            "#faf0be": "Blond",
            "#0000ff": "Blue",
            "#a2a2d0": "Blue Bell",
            "#6699cc": "Blue Gray",
            "#0d98ba": "Blue green",
            "#8a2be2": "Blue violet",
            "#de5d83": "Blush",
            "#79443b": "Bole",
            "#0095b6": "Bondi blue",
            "#e3dac9": "Bone",
            "#cc0000": "Boston University Red",
            "#006a4e": "Bottle green",
            "#873260": "Boysenberry",
            "#0070ff": "Brandeis blue",
            "#b5a642": "Brass",
            "#cb4154": "Brick red",
            "#1dacd6": "Bright cerulean",
            "#66ff00": "Bright green",
            "#bf94e4": "Bright lavender",
            "#c32148": "Bright maroon",
            "#ff007f": "Rose",
            "#08e8de": "Bright turquoise",
            "#d19fe8": "Bright ube",
            "#f4bbff": "Electric lavender",
            "#ff55a3": "Brilliant rose",
            "#fb607f": "Brink pink",
            "#004225": "British racing green",
            "#cd7f32": "Bronze",
            "#ffc1cc": "Bubble gum",
            "#e7feff": "Bubbles",
            "#f0dc82": "Buff",
            "#480607": "Bulgarian rose",
            "#800020": "Burgundy",
            "#deb887": "Burlywood",
            "#cc5500": "Burnt orange",
            "#e97451": "Burnt sienna",
            "#8a3324": "Burnt umber",
            "#bd33a4": "Byzantine",
            "#702963": "Byzantium",
            "#007aa5": "CG Blue",
            "#e03c31": "CG Red",
            "#536872": "Cadet",
            "#5f9ea0": "Cadet blue",
            "#91a3b0": "Cadet grey",
            "#006b3c": "Cadmium green",
            "#ed872d": "Cadmium orange",
            "#e30022": "Cadmium red",
            "#fff600": "Cadmium yellow",
            "#a67b5b": "French beige",
            "#4b3621": "Café noir",
            "#1e4d2b": "Cal Poly Pomona green",
            "#a3c1ad": "Cambridge Blue",
            "#c19a6b": "Lion",
            "#78866b": "Camouflage green",
            "#ffff99": "Canary",
            "#ffef00": "Canary yellow",
            "#ff0800": "Candy apple red",
            "#e4717a": "Candy pink",
            "#00bfff": "Deep sky blue",
            "#592720": "Caput mortuum",
            "#c41e3a": "Cardinal",
            "#00cc99": "Caribbean green",
            "#ff0040": "Carmine",
            "#eb4c42": "Carmine pink",
            "#ff0038": "Carmine red",
            "#ffa6c9": "Carnation pink",
            "#b31b1b": "Cornell Red",
            "#99badd": "Carolina blue",
            "#ed9121": "Carrot orange",
            "#ace1af": "Celadon",
            "#b2ffff": "Celeste",
            "#4997d0": "Celestial blue",
            "#de3163": "Cherry",
            "#ec3b83": "Cerise pink",
            "#007ba7": "Cerulean",
            "#2a52be": "Cerulean blue",
            "#a0785a": "Chamoisee",
            "#fad6a5": "Sunset",
            "#36454f": "Charcoal",
            "#7fff00": "Chartreuse",
            "#ffb7c5": "Cherry blossom pink",
            "#cd5c5c": "Indian red",
            "#d2691e": "Cocoa brown",
            "#ffa700": "Chrome yellow",
            "#98817b": "Cinereous",
            "#e34234": "Vermilion",
            "#e4d00a": "Citrine",
            "#fbcce7": "Classic rose",
            "#0047ab": "Cobalt",
            "#6f4e37": "Coffee",
            "#9bddff": "Columbia blue",
            "#002e63": "Cool black",
            "#8c92ac": "Cool grey",
            "#b87333": "Copper",
            "#996666": "Copper rose",
            "#ff3800": "Coquelicot",
            "#ff7f50": "Coral",
            "#f88379": "Coral pink",
            "#ff4040": "Coral red",
            "#893f45": "Cordovan",
            "#fbec5d": "Maize",
            "#9aceeb": "Cornflower",
            "#6495ed": "Cornflower blue",
            "#fff8dc": "Cornsilk",
            "#fff8e7": "Cosmic latte",
            "#ffbcd9": "Cotton candy",
            "#fffdd0": "Cream",
            "#dc143c": "Crimson",
            "#990000": "USC Cardinal",
            "#be0032": "Crimson glory",
            "#ffff31": "Daffodil",
            "#f0e130": "Dandelion",
            "#00008b": "Dark blue",
            "#654321": "Otter brown",
            "#5d3954": "Dark byzantium",
            "#a40000": "Dark candy apple red",
            "#08457e": "Dark cerulean",
            "#986960": "Dark chestnut",
            "#cd5b45": "Dark coral",
            "#008b8b": "Dark cyan",
            "#536878": "Payne grey",
            "#b8860b": "Dark goldenrod",
            "#a9a9a9": "Dark gray",
            "#013220": "Dark green",
            "#1a2421": "Dark jungle green",
            "#bdb76b": "Dark khaki",
            "#483c32": "Taupe",
            "#734f96": "Dark lavender",
            "#8b008b": "Dark magenta",
            "#003366": "Dark midnight blue",
            "#556b2f": "Dark olive green",
            "#ff8c00": "Dark orange",
            "#9932cc": "Dark orchid",
            "#779ecb": "Dark pastel blue",
            "#03c03c": "Dark pastel green",
            "#966fd6": "Dark pastel purple",
            "#c23b22": "Dark pastel red",
            "#e75480": "Dark pink",
            "#003399": "Smalt",
            "#872657": "Dark raspberry",
            "#8b0000": "Dark red",
            "#e9967a": "Dark salmon",
            "#560319": "Dark scarlet",
            "#8fbc8f": "Dark sea green",
            "#3c1414": "Dark sienna",
            "#483d8b": "Dark slate blue",
            "#2f4f4f": "Dark slate gray",
            "#177245": "Dark spring green",
            "#918151": "Dark tan",
            "#ffa812": "Dark tangerine",
            "#cc4e5c": "Dark terra cotta",
            "#00ced1": "Dark turquoise",
            "#9400d3": "Dark violet",
            "#00693e": "Dartmouth green",
            "#555555": "Davy grey",
            "#d70a53": "Debian red",
            "#a9203e": "Deep carmine",
            "#ef3038": "Deep carmine pink",
            "#e9692c": "Deep carrot orange",
            "#da3287": "Deep cerise",
            "#b94e48": "Deep chestnut",
            "#704241": "Deep coffee",
            "#c154c1": "Deep fuchsia",
            "#004b49": "Deep jungle green",
            "#9955bb": "Deep lilac",
            "#cc00cc": "Deep magenta",
            "#ffcba4": "Deep peach",
            "#ff1493": "Fluorescent pink",
            "#ff9933": "Deep saffron",
            "#1560bd": "Denim",
            "#edc9af": "Desert sand",
            "#696969": "Dim gray",
            "#1e90ff": "Dodger blue",
            "#d71868": "Dogwood rose",
            "#85bb65": "Dollar bill",
            "#967117": "Sandy taupe",
            "#00009c": "Duke blue",
            "#e1a95f": "Earth yellow",
            "#c2b280": "Sand",
            "#614051": "Eggplant",
            "#f0ead6": "Eggshell",
            "#1034a6": "Egyptian blue",
            "#7df9ff": "Electric blue",
            "#ff003f": "Electric crimson",
            "#00ff00": "Green",
            "#6f00ff": "Electric indigo",
            "#ccff00": "Fluorescent yellow",
            "#bf00ff": "Electric purple",
            "#3f00ff": "Electric ultramarine",
            "#8f00ff": "Electric violet",
            "#ffff00": "Yellow",
            "#50c878": "Paris Green",
            "#96c8a2": "Eton blue",
            "#801818": "Falu red",
            "#ff00ff": "Magenta",
            "#b53389": "Fandango",
            "#f400a1": "Hollywood cerise",
            "#e5aa70": "Fawn",
            "#4d5d53": "Feldgrau",
            "#71bc78": "Fern",
            "#4f7942": "Fern green",
            "#ff2800": "Ferrari Red",
            "#6c541e": "Field drab",
            "#ce2029": "Fire engine red",
            "#b22222": "Firebrick",
            "#e25822": "Flame",
            "#fc8eac": "Flamingo pink",
            "#f7e98e": "Flavescent",
            "#eedc82": "Flax",
            "#fffaf0": "Floral white",
            "#ff004f": "Folly",
            "#228b22": "Forest green",
            "#0072bb": "French blue",
            "#86608e": "French lilac",
            "#f64a8a": "French rose",
            "#ff77ff": "Fuchsia pink",
            "#e48400": "Fulvous",
            "#cc6666": "Fuzzy Wuzzy",
            "#dcdcdc": "Gainsboro",
            "#e49b0f": "Gamboge",
            "#f8f8ff": "Ghost white",
            "#b06500": "Ginger",
            "#6082b6": "Glaucous",
            "#e6e8fa": "Glitter",
            "#ffd700": "Gold",
            "#996515": "Golden brown",
            "#fcc200": "Golden poppy",
            "#ffdf00": "Golden yellow",
            "#daa520": "Goldenrod",
            "#a8e4a0": "Granny Smith Apple",
            "#808080": "Trolley Grey",
            "#465945": "Gray asparagus",
            "#1164b4": "Green Blue",
            "#adff2f": "Green yellow",
            "#a99a86": "Grullo",
            "#00ff7f": "Spring green",
            "#663854": "Halayà úbe",
            "#446ccf": "Han blue",
            "#5218fa": "Han purple",
            "#3fff00": "Harlequin",
            "#c90016": "Harvard crimson",
            "#da9100": "Harvest Gold",
            "#808000": "Olive",
            "#df73ff": "Heliotrope",
            "#f0fff0": "Honeydew",
            "#49796b": "Hooker green",
            "#ff1dce": "Hot magenta",
            "#ff69b4": "Hot pink",
            "#355e3b": "Hunter green",
            "#fcf75e": "Icterine",
            "#b2ec5d": "Inchworm",
            "#138808": "India green",
            "#e3a857": "Indian yellow",
            "#4b0082": "Indigo",
            "#002fa7": "International Klein Blue",
            "#ff4f00": "International orange",
            "#5a4fcf": "Iris",
            "#f4f0ec": "Isabelline",
            "#009000": "Islamic green",
            "#fffff0": "Ivory",
            "#00a86b": "Jade",
            "#f8de7e": "Jasmine",
            "#d73b3e": "Jasper",
            "#a50b5e": "Jazzberry jam",
            "#fada5e": "Stil de grain yellow",
            "#bdda57": "June bud",
            "#29ab87": "Jungle green",
            "#e8000d": "KU Crimson",
            "#4cbb17": "Kelly green",
            "#c3b091": "Khaki",
            "#087830": "La Salle Green",
            "#d6cadd": "Languid lavender",
            "#26619c": "Lapis lazuli",
            "#fefe22": "Laser Lemon",
            "#a9ba9d": "Laurel green",
            "#cf1020": "Lava",
            "#e6e6fa": "Lavender mist",
            "#ccccff": "Periwinkle",
            "#fff0f5": "Lavender blush",
            "#c4c3d0": "Lavender gray",
            "#9457eb": "Lavender indigo",
            "#ee82ee": "Violet",
            "#fbaed2": "Lavender pink",
            "#967bb6": "Lavender purple",
            "#fba0e3": "Lavender rose",
            "#7cfc00": "Lawn green",
            "#fff700": "Lemon",
            "#fff44f": "Lemon Yellow",
            "#fffacd": "Lemon chiffon",
            "#bfff00": "Lime",
            "#f56991": "Light Crimson",
            "#e68fac": "Light Thulian pink",
            "#fdd5b1": "Light apricot",
            "#add8e6": "Light blue",
            "#b5651d": "Light brown",
            "#e66771": "Light carmine pink",
            "#f08080": "Light coral",
            "#93ccea": "Light cornflower blue",
            "#e0ffff": "Light cyan",
            "#f984ef": "Light fuchsia pink",
            "#fafad2": "Light goldenrod yellow",
            "#d3d3d3": "Light gray",
            "#90ee90": "Light green",
            "#f0e68c": "Light khaki",
            "#b19cd9": "Light pastel purple",
            "#ffb6c1": "Light pink",
            "#ffa07a": "Light salmon",
            "#ff9999": "Light salmon pink",
            "#20b2aa": "Light sea green",
            "#87cefa": "Light sky blue",
            "#778899": "Light slate gray",
            "#b38b6d": "Light taupe",
            "#ffffed": "Light yellow",
            "#c8a2c8": "Lilac",
            "#32cd32": "Lime green",
            "#195905": "Lincoln green",
            "#faf0e6": "Linen",
            "#534b4f": "Liver",
            "#e62020": "Lust",
            "#18453b": "MSU Green",
            "#ffbd88": "Macaroni and Cheese",
            "#aaf0d1": "Magic mint",
            "#f8f4ff": "Magnolia",
            "#c04000": "Mahogany",
            "#6050dc": "Majorelle Blue",
            "#0bda51": "Malachite",
            "#979aaa": "Manatee",
            "#ff8243": "Mango Tango",
            "#74c365": "Mantis",
            "#800000": "Maroon",
            "#e0b0ff": "Mauve",
            "#915f6d": "Raspberry glace",
            "#ef98aa": "Mauvelous",
            "#73c2fb": "Maya blue",
            "#e5b73b": "Meat brown",
            "#0067a5": "Medium Persian blue",
            "#66ddaa": "Medium aquamarine",
            "#0000cd": "Medium blue",
            "#e2062c": "Medium candy apple red",
            "#af4035": "Pale carmine",
            "#f3e5ab": "Vanilla",
            "#035096": "Medium electric blue",
            "#1c352d": "Medium jungle green",
            "#dda0dd": "Plum",
            "#ba55d3": "Medium orchid",
            "#9370db": "Medium purple",
            "#bb3385": "Medium red violet",
            "#3cb371": "Medium sea green",
            "#7b68ee": "Medium slate blue",
            "#c9dc87": "Medium spring bud",
            "#00fa9a": "Medium spring green",
            "#674c47": "Medium taupe",
            "#0054b4": "Medium teal blue",
            "#48d1cc": "Medium turquoise",
            "#c71585": "Red violet",
            "#fdbcb4": "Melon",
            "#191970": "Midnight blue",
            "#004953": "Midnight green",
            "#ffc40c": "Mikado yellow",
            "#3eb489": "Mint",
            "#f5fffa": "Mint cream",
            "#98ff98": "Mint green",
            "#ffe4e1": "Misty rose",
            "#73a9c2": "Moonstone blue",
            "#ae0c00": "Mordant red 19",
            "#addfad": "Moss green",
            "#30ba8f": "Mountain Meadow",
            "#997a8d": "Mountbatten pink",
            "#c54b8c": "Mulberry",
            "#ffdb58": "Mustard",
            "#21421e": "Myrtle",
            "#f6adc6": "Nadeshiko pink",
            "#2a8000": "Napier green",
            "#ffdead": "Navajo white",
            "#000080": "Navy blue",
            "#ffa343": "Neon Carrot",
            "#fe59c2": "Neon fuchsia",
            "#39ff14": "Neon green",
            "#a4dded": "Non-photo blue",
            "#059033": "North Texas Green",
            "#0077be": "Ocean Boat Blue",
            "#cc7722": "Ochre",
            "#cfb53b": "Old gold",
            "#fdf5e6": "Old lace",
            "#796878": "Old lavender",
            "#673147": "Old mauve",
            "#c08081": "Old rose",
            "#6b8e23": "Olive Drab",
            "#bab86c": "Olive Green",
            "#9ab973": "Olivine",
            "#0f0f0f": "Onyx",
            "#b784a7": "Opera mauve",
            "#ffa500": "Orange",
            "#f8d568": "Orange Yellow",
            "#ff9f00": "Orange peel",
            "#ff4500": "Orange red",
            "#da70d6": "Orchid",
            "#414a4c": "Outer Space",
            "#ff6e4a": "Outrageous Orange",
            "#002147": "Oxford Blue",
            "#1ca9c9": "Pacific Blue",
            "#006600": "Pakistan green",
            "#273be2": "Palatinate blue",
            "#682860": "Palatinate purple",
            "#afeeee": "Pale blue",
            "#987654": "Pale brown",
            "#9bc4e2": "Pale cerulean",
            "#ddadaf": "Pale chestnut",
            "#da8a67": "Pale copper",
            "#abcdef": "Pale cornflower blue",
            "#e6be8a": "Pale gold",
            "#eee8aa": "Pale goldenrod",
            "#98fb98": "Pale green",
            "#dcd0ff": "Pale lavender",
            "#f984e5": "Pale magenta",
            "#fadadd": "Pale pink",
            "#db7093": "Pale violet red",
            "#96ded1": "Pale robin egg blue",
            "#c9c0bb": "Pale silver",
            "#ecebbd": "Pale spring bud",
            "#bc987e": "Pale taupe",
            "#78184a": "Pansy purple",
            "#ffefd5": "Papaya whip",
            "#aec6cf": "Pastel blue",
            "#836953": "Pastel brown",
            "#cfcfc4": "Pastel gray",
            "#77dd77": "Pastel green",
            "#f49ac2": "Pastel magenta",
            "#ffb347": "Pastel orange",
            "#ffd1dc": "Pastel pink",
            "#b39eb5": "Pastel purple",
            "#ff6961": "Pastel red",
            "#cb99c9": "Pastel violet",
            "#fdfd96": "Pastel yellow",
            "#800080": "Purple",
            "#ffe5b4": "Peach",
            "#ffdab9": "Peach puff",
            "#fadfad": "Peach yellow",
            "#d1e231": "Pear",
            "#eae0c8": "Pearl",
            "#88d8c0": "Pearl Aqua",
            "#e6e200": "Peridot",
            "#1c39bb": "Persian blue",
            "#32127a": "Persian indigo",
            "#d99058": "Persian orange",
            "#f77fbe": "Persian pink",
            "#701c1c": "Persian plum",
            "#cc3333": "Persian red",
            "#fe28a2": "Persian rose",
            "#df00ff": "Psychedelic purple",
            "#000f89": "Phthalo blue",
            "#123524": "Phthalo green",
            "#fddde6": "Piggy pink",
            "#01796f": "Pine green",
            "#ffc0cb": "Pink",
            "#fc74fd": "Pink Flamingo",
            "#f78fa7": "Pink Sherbet",
            "#e7accf": "Pink pearl",
            "#93c572": "Pistachio",
            "#e5e4e2": "Platinum",
            "#ff5a36": "Portland Orange",
            "#b0e0e6": "Powder blue",
            "#ff8f00": "Princeton orange",
            "#003153": "Prussian blue",
            "#cc8899": "Puce",
            "#ff7518": "Pumpkin",
            "#69359c": "Purple Heart",
            "#9d81ba": "Purple Mountain's Majesty",
            "#9678b6": "Purple mountain majesty",
            "#fe4eda": "Purple pizzazz",
            "#50404d": "Purple taupe",
            "#ff355e": "Radical Red",
            "#e30b5d": "Raspberry",
            "#e25098": "Raspberry pink",
            "#b3446c": "Raspberry rose",
            "#d68a59": "Raw Sienna",
            "#ff33cc": "Razzle dazzle rose",
            "#e3256b": "Razzmatazz",
            "#ff0000": "Red",
            "#ff5349": "Red Orange",
            "#004040": "Rich black",
            "#d70040": "Rich carmine",
            "#0892d0": "Rich electric blue",
            "#b666d2": "Rich lilac",
            "#b03060": "Rich maroon",
            "#414833": "Rifle green",
            "#1fcecb": "Robin's Egg Blue",
            "#f9429e": "Rose bonbon",
            "#674846": "Rose ebony",
            "#b76e79": "Rose gold",
            "#ff66cc": "Rose pink",
            "#aa98a9": "Rose quartz",
            "#905d5d": "Rose taupe",
            "#ab4e52": "Rose vale",
            "#65000b": "Rosewood",
            "#d40000": "Rosso corsa",
            "#bc8f8f": "Rosy brown",
            "#0038a8": "Royal azure",
            "#4169e1": "Royal blue",
            "#ca2c92": "Royal fuchsia",
            "#7851a9": "Royal purple",
            "#e0115f": "Ruby",
            "#ff0028": "Ruddy",
            "#bb6528": "Ruddy brown",
            "#e18e96": "Ruddy pink",
            "#a81c07": "Rufous",
            "#80461b": "Russet",
            "#b7410e": "Rust",
            "#00563f": "Sacramento State green",
            "#8b4513": "Saddle brown",
            "#ff6700": "Safety orange",
            "#f4c430": "Saffron",
            "#23297a": "Saint Patrick Blue",
            "#ff8c69": "Salmon",
            "#ff91a4": "Salmon pink",
            "#ecd540": "Sandstorm",
            "#f4a460": "Sandy brown",
            "#507d2a": "Sap green",
            "#0f52ba": "Sapphire",
            "#cba135": "Satin sheen gold",
            "#ff2400": "Scarlet",
            "#ffd800": "School bus yellow",
            "#76ff7a": "Screamin Green",
            "#006994": "Sea blue",
            "#2e8b57": "Sea green",
            "#321414": "Seal brown",
            "#fff5ee": "Seashell",
            "#ffba00": "Selective yellow",
            "#704214": "Sepia",
            "#8a795d": "Shadow",
            "#45cea2": "Shamrock",
            "#009e60": "Shamrock green",
            "#fc0fc0": "Shocking pink",
            "#882d17": "Sienna",
            "#c0c0c0": "Silver",
            "#cb410b": "Sinopia",
            "#007474": "Skobeloff",
            "#87ceeb": "Sky blue",
            "#cf71af": "Sky magenta",
            "#6a5acd": "Slate blue",
            "#708090": "Slate gray",
            "#933d41": "Smokey topaz",
            "#100c08": "Smoky black",
            "#fffafa": "Snow",
            "#0fc0fc": "Spiro Disco Ball",
            "#a7fc00": "Spring bud",
            "#4682b4": "Steel blue",
            "#008080": "Teal",
            "#e4d96f": "Straw",
            "#ffcc33": "Sunglow",
            "#fd5e53": "Sunset Orange",
            "#d2b48c": "Tan",
            "#f94d00": "Tangelo",
            "#f28500": "Tangerine",
            "#ffcc00": "USC Gold",
            "#8b8589": "Taupe gray",
            "#cd5700": "Tawny",
            "#d0f0c0": "Tea green",
            "#367588": "Teal blue",
            "#006d5b": "Teal green",
            "#e2725b": "Terra cotta",
            "#d8bfd8": "Thistle",
            "#de6fa1": "Thulian pink",
            "#fc89ac": "Tickle Me Pink",
            "#0abab5": "Tiffany Blue",
            "#e08d3c": "Tiger eye",
            "#dbd7d2": "Timberwolf",
            "#eee600": "Titanium yellow",
            "#ff6347": "Tomato",
            "#746cc0": "Toolbox",
            "#ffc87c": "Topaz",
            "#fd0e35": "Tractor red",
            "#00755e": "Tropical rain forest",
            "#0073cf": "True Blue",
            "#417dc1": "Tufts Blue",
            "#deaa88": "Tumbleweed",
            "#b57281": "Turkish rose",
            "#30d5c8": "Turquoise",
            "#00ffef": "Turquoise blue",
            "#a0d6b4": "Turquoise green",
            "#66424d": "Tuscan red",
            "#8a496b": "Twilight lavender",
            "#66023c": "Tyrian purple",
            "#0033aa": "UA blue",
            "#d9004c": "UA red",
            "#536895": "UCLA Blue",
            "#ffb300": "UCLA Gold",
            "#3cd070": "UFO Green",
            "#014421": "UP Forest green",
            "#7b1113": "UP Maroon",
            "#8878c3": "Ube",
            "#ff6fff": "Ultra pink",
            "#120a8f": "Ultramarine",
            "#4166f5": "Ultramarine blue",
            "#635147": "Umber",
            "#5b92e5": "United Nations blue",
            "#b78727": "University of California Gold",
            "#ffff66": "Unmellow Yellow",
            "#ae2029": "Upsdell red",
            "#e1ad21": "Urobilin",
            "#d3003f": "Utah Crimson",
            "#c5b358": "Vegas gold",
            "#c80815": "Venetian red",
            "#43b3ae": "Verdigris",
            "#a020f0": "Veronica",
            "#324ab2": "Violet Blue",
            "#f75394": "Violet Red",
            "#40826d": "Viridian",
            "#922724": "Vivid auburn",
            "#9f1d35": "Vivid burgundy",
            "#da1d81": "Vivid cerise",
            "#ffa089": "Vivid tangerine",
            "#9f00ff": "Vivid violet",
            "#004242": "Warm black",
            "#645452": "Wenge",
            "#f5deb3": "Wheat",
            "#ffffff": "White",
            "#f5f5f5": "White smoke",
            "#ff43a4": "Wild Strawberry",
            "#fc6c85": "Wild Watermelon",
            "#a2add0": "Wild blue yonder",
            "#722f37": "Wine",
            "#c9a0dc": "Wisteria",
            "#738678": "Xanadu",
            "#0f4d92": "Yale Blue",
            "#ffae42": "Yellow Orange",
            "#9acd32": "Yellow green",
            "#0014a8": "Zaffre",
            "#2c1608": "Zinnwaldite brown",
            "#111111": "Night",
            "#138808": "India green",
            "#177245": "Dark spring green",
            "#191970": "Midnight blue",
            "#242124": "Raisin black",
            "#301934": "Dark purple",
            "#317873": "Myrtle green",
            "#343434": "Jet",
            "#353839": "Onyx",
            "#512888": "Tekhelet",
            "#536878": "Payne's gray",
            "#555555": "Davy's gray",
            "#568203": "Avocado",
            "#592720": "Caput mortuum",
            "#614051": "Eggplant",
            "#635147": "Umber",
            "#645394": "Ultra Violet",
            "#645452": "Wenge",
            "#660000": "Blood red",
            "#663399": "Rebecca purple",
            "#674846": "Rose ebony",
            "#682860": "Palatinate",
            "#683068": "Finn",
            "#696969": "Dim gray",
            "#702963": "Byzantium",
            "#704214": "Sepia",
            "#708090": "Slate gray",
            "#733635": "Garnet",
            "#800000": "Maroon",
            "#800020": "Burgundy",
            "#800080": "Purple",
            "#801818": "Falu red",
            "#808000": "Olive",
            "#808080": "Gray",
            "#841617": "OU crimson",
            "#848482": "Battleship gray",
            "#856088": "Chinese violet",
            "#880085": "Mardi Gras",
            "#926644": "Raw umber",
            "#954535": "Chestnut",
            "#960018": "Carmine",
            "#990000": "Penn red",
            "#993300": "Brown",
            "#996515": "Golden brown",
            "#AB274F": "Amaranth purple",
            "#7C0902": "Barn red",
            "#FE6F5E": "Bittersweet",
            "#BF4F51": "Bittersweet shimmer",
            "#FB607F": "Bright pink (Crayola)",
            "#FF0800": "Candy apple red",
            "#FDBCB4": "Cantaloupe melon",
            "#C51E3A": "Cardinal",
            "#DE3163": "Cerise",
            "#E23D28": "Chili red",
            "#58111A": "Chocolate cosmos",
            "#E44D2E": "Cinnabar",
            "#7F1734": "Claret",
            "#FF3800": "Coquelicot",
            "#F88379": "Coral pink",
            "#893F45": "Cordovan",
            "#B31B1B": "Cornell red",
            "#DC143C": "Crimson",
            "#8B0000": "Dark red",
            "#B22222": "Fire brick",
            "#CE2029": "Fire engine red",
            "#FF004F": "Folly",
            "#ED2939": "Imperial red",
            "#CD5C5C": "Indian red",
            "#D05340": "Jasper",
            "#F08080": "Light coral",
            "#FF7F7F": "Light red",
            "#A50021": "Madder",
            "#C04000": "Mahogany",
            "#FFE4E1": "Misty rose",
            "#FE0000": "Off Red (RGB)",
            "#C08081": "Old rose",
            "#CC3333": "Persian red",
            "#FFC0CB": "Pink",
            "#DC343B": "Poppy",
            "#FF0000": "Red",
            "#A52A2A": "Red Brown",
            "#ED1B24": "Red (CMYK)",
            "#EE204E": "Red (Crayola)",
            "#F2003C": "Red (Munsell)",
            "#C40234": "Red (NCS)",
            "#ED2839": "Red (Pantone)",
            "#AB4E52": "Redwood",
            "#E60026": "Rojo",
            "#FF0080": "Rose",
            "#C21E56": "Rose red",
            "#905D5D": "Rose taupe",
            "#65000B": "Rosewood",
            "#BC8F8F": "Rosy brown",
            "#B7410E": "Rust",
            "#DA2C43": "Rusty red",
            "#FA8072": "Salmon",
            "#FF91A4": "Salmon pink",
            "#FF2400": "Scarlet",
            "#D44500": "Syracuse Red Orange",
            "#F4C2C2": "Tea rose (red)",
            "#FF6347": "Tomato",
            "#A91101": "Turkey red",
            "#E34234": "Vermilion",
            "#722F37": "Wine",
            "#FF4F00": "Aerospace orange",
            "#C46210": "Alloy orange",
            "#FFBF00": "Amber",
            "#FBCEB1": "Apricot",
            "#FF9966": "Atomic tangerine",
            "#964B00": "Brown",
            "#BF5700": "Burnt orange",
            "#E09540": "Butterscotch",
            "#ED9121": "Carrot orange",
            "#F7E7CE": "Champagne (color)",
            "#FF7F50": "Coral",
            "#FF8C00": "Dark orange (web)",
            "#EDC9AF": "Desert sand (color)",
            "#BA160C": "Engineering orange",
            "#E25822": "Flame",
            "#FE5A1D": "Giants orange",
            "#FFD700": "Gold",
            "#F04A00": "Golden Gate Bridge",
            "#DAA520": "Goldenrod",
            "#E8AC41": "Hunyadi yellow",
            "#FED8B1": "Light orange",
            "#FF7538": "Orange (Crayola)",
            "#FF5800": "Orange (Pantone)",
            "#FFA000": "Orange peel",
            "#FFA500": "Orange (web)",
            "#FF8000": "Orange (wheel)",
            "#FFEFD5": "Papaya whip",
            "#FFE5B4": "Peach",
            "#D99058": "Persian orange",
            "#EC5800": "Persimmon",
            "#FF8F00": "Princeton orange",
            "#FF7518": "Pumpkin",
            "#FF7900": "Safety orange",
            "#F4C430": "Saffron",
            "#E86100": "Spanish orange",
            "#F94D00": "Tangelo",
            "#F28500": "Tangerine",
            "#CD5700": "Tawny",
            "#B56917": "Tiger’s Eye",
            "#FF8200": "UT orange",
            "#F1B42F": "Xanthous",
            "#EFDECD": "Almond",
            "#9F8170": "Beaver",
            "#F5F5DC": "Beige",
            "#3D2B1F": "Bistre",
            "#3D0C02": "Black bean",
            "#3B3C36": "Black olive",
            "#79443B": "Bole",
            "#E3DAC9": "Bone",
            "#CD7F32": "Bronze",
            "#AF6E4D": "Brown sugar",
            "#DAA06D": "Buff",
            "#E97451": "Burnt sienna",
            "#8A3324": "Burnt umber",
            "#C19A6B": "Camel",
            "#CC7F3B": "Caramel",
            "#A0785A": "Chamoisee",
            "#7B3F00": "Chocolate",
            "#DDD06A": "Citron",
            "#D2691E": "Cocoa Brown",
            "#6F4E37": "Coffee",
            "#B87333": "Copper",
            "#81613E": "Coyote",
            "#4A412A": "Drab dark brown",
            "#DAC8AE": "Dun",
            "#E1A95F": "Earth yellow",
            "#C2B280": "Ecru",
            "#E5AA70": "Fawn",
            "#6C541E": "Field drab",
            "#E48400": "Fulvous",
            "#DA9100": "Harvest gold",
            "#C3B091": "Khaki",
            "#6B4423": "Kobicha",
            "#674C47": "Liver",
            "#CC7722": "Ochre",
            "#A45A52": "Redwood",
            "#A81C07": "Rufous",
            "#80461B": "Russet",
            "#F4A460": "Sandy brown",
            "#CBA135": "Satin sheen gold",
            "#59260B": "Seal brown",
            "#882D17": "Sienna",
            "#CB410B": "Sinopia",
            "#D2B48C": "Tan",
            "#483C32": "Taupe",
            "#44362F": "Van Dyke",
            "#5C5248": "Walnut brown",
            "#F5DEB3": "Wheat",
            "#E9D66B": "Arylide yellow",
            "#FDEE00": "Aureolin",
            "#E0AB76": "Buff",
            "#FFEF00": "Canary",
            "#DFFF00": "Chartreuse",
            "#E4D00A": "Citrine",
            "#FFF8E7": "Cosmic latte",
            "#FFFDD0": "Cream",
            "#B8860B": "Dark goldenrod",
            "#CDB280": "Ecru",
            "#EEDC82": "Flax",
            "#EF9B0F": "Gamboge",
            "#D4AF37": "Gold (metallic)",
            "#E6A817": "Harvest gold",
            "#FCF75E": "Icterine",
            "#FFFFF0": "Ivory",
            "#F8DE7E": "Jasmine",
            "#FACA16": "Jonquil",
            "#FFFACD": "Lemon chiffon",
            "#E3FF00": "Lemon Lime",
            "#FFFFE0": "Light yellow",
            "#FBEC5D": "Maize",
            "#FFC40C": "Mikado yellow",
            "#E3F988": "Mindaro",
            "#FFDB58": "Mustard",
            "#FADA5E": "Naples yellow",
            "#FFDEAD": "Navajo white",
            "#CFB53B": "Old gold",
            "#FADFAD": "Peach Yellow",
            "#D1E231": "Pear",
            "#F4C431": "Saffron",
            "#FFD800": "School bus yellow",
            "#FFBA00": "Selective yellow",
            "#E4D96F": "Straw",
            "#FFCC33": "Sunglow",
            "#FAD6A5": "Sunset",
            "#F3E5AB": "Vanilla",
            "#FFFF00": "Yellow",
            "#8DB600": "Apple green",
            "#00FFBF": "Aquamarine",
            "#7BA05B": "Asparagus",
            "#66FF00": "Bright green",
            "#004225": "British racing green",
            "#1B4D3E": "Brunswick green",
            "#1E4D2B": "Cal Poly green",
            "#00563B": "Castleton green",
            "#ACE1AF": "Celadon",
            "#80FF00": "Chartreuse",
            "#00FFFF": "Cyan",
            "#013220": "Dark green",
            "#4A5D23": "Dark moss green",
            "#03C03C": "Dark pastel green",
            "#00693E": "Dartmouth green",
            "#50C878": "Emerald",
            "#00FF40": "Erin",
            "#4F7942": "Fern green",
            "#228B22": "Forest green",
            "#00FF00": "Green",
            "#ADFF2F": "Green Yellow",
            "#3FFF00": "Harlequin",
            "#F0FFF0": "Honeydew",
            "#49796B": "Hooker's green",
            "#355E3B": "Hunter green",
            "#00A86B": "Jade",
            "#29AB87": "Jungle green",
            "#4CBB17": "Kelly green",
            "#7CFC00": "Lawn green",
            "#90EE90": "Light green",
            "#C0FF00": "Lime",
            "#32CD32": "Lime green",
            "#0BDA51": "Malachite",
            "#74C365": "Mantis",
            "#004953": "Midnight green",
            "#3EB489": "Mint",
            "#8A9A5B": "Moss green",
            "#39FF14": "Neon green",
            "#008000": "Office green",
            "#9AB973": "Olivine",
            "#00401A": "Pakistan green",
            "#00A693": "Persian green",
            "#00A550": "Pigment green",
            "#01796F": "Pine green",
            "#93C572": "Pistachio",
            "#6C7C59": "Reseda green",
            "#00CCCC": "Robin egg blue",
            "#BCB88A": "Sage",
            "#76FF7A": "Screamin' green",
            "#2E8B57": "Sea green",
            "#55DD33": "SGBUS green",
            "#009E60": "Shamrock green",
            "#A7FC00": "Spring bud",
            "#00FF7F": "Spring green",
            "#D0F0C0": "Tea green",
            "#008080": "Teal",
            "#40E0D0": "Turquoise",
            "#40826D": "Viridian",
            "#9ACD32": "Yellow Green",
            "#F0F8FF": "Alice blue",
            "#0FFFFF": "Aqua",
            "#7FFFD4": "Aquamarine",
            "#007FFF": "Azure",
            "#F0FFFF": "Azure (web)",
            "#0D98BA": "Blue Green",
            "#00BFFF": "Capri",
            "#006D6F": "Caribbean Current",
            "#B2FFFF": "Celeste",
            "#007BA7": "Cerulean",
            "#008B8B": "Dark cyan",
            "#7DF9FF": "Electric blue",
            "#15F4EE": "Fluorescent cyan",
            "#3AB09E": "Keppel",
            "#99FFFF": "Ice blue",
            "#E0FFFF": "Light cyan",
            "#20B2AA": "Light sea green",
            "#DFFFFD": "Mint green",
            "#3AA8C1": "Moonstone",
            "#1CA9C9": "Pacific cyan",
            "#00B7EB": "Process Cyan",
            "#007A74": "Skobeloff",
            "#81D8D0": "Tiffany Blue",
            "#43B3AE": "Verdigris",
            "#00CCFF": "Vivid sky blue",
            "#39A78D": "Zomp",
            "#00B9E8": "Aero",
            "#5D8AA8": "Air Force blue",
            "#72A0C1": "Air superiority blue",
            "#6CB4EE": "Argentinian Blue",
            "#0070BB": "Azul",
            "#89CFF0": "Baby blue",
            "#003262": "Berkeley Blue",
            "#2072AF": "Bice blue",
            "#318CE7": "Bleu de France",
            "#0000FF": "Blue",
            "#6699CC": "Blue Gray",
            "#0095B6": "Bondi blue",
            "#0070FF": "Brandeis blue",
            "#3457D5": "Byzantine blue",
            "#85B09A": "Cambridge blue",
            "#7BAFD4": "Carolina blue",
            "#4997D0": "Celestial Blue",
            "#246BCE": "Celtic Blue",
            "#468FEA": "Chefchaouen Blue",
            "#3B00DB": "Chrysler blue",
            "#0047AB": "Cobalt blue",
            "#B9D9EB": "Columbia blue",
            "#6495ED": "Cornflower blue",
            "#1F75FE": "Blue (Crayola)",
            "#0000B8": "Dark blue",
            "#1F305E": "Delft Blue",
            "#1560BD": "Denim",
            "#1E90FF": "Dodger blue",
            "#00009C": "Duke blue",
            "#1034A6": "Egyptian blue",
            "#16166B": "Federal blue",
            "#6082B6": "Glaucous",
            "#1164B4": "Green Blue",
            "#6F00FF": "Electric indigo",
            "#0072BB": "French Blue",
            "#4B0082": "Indigo",
            "#00416A": "Indigo dye",
            "#002FA7": "International Klein Blue",
            "#8AB9F1": "Jordy Blue",
            "#26619C": "Lapis Lazuli",
            "#ADD8E6": "Light blue",
            "#87CEFA": "Light Sky Blue",
            "#6050DC": "Majorelle Blue",
            "#2B4593": "Marian blue",
            "#73C2FB": "Maya blue",
            "#0000CD": "Medium blue",
            "#7B68EE": "Medium slate blue",
            "#0093AF": "Blue (Munsell)",
            "#000080": "Navy blue",
            "#0087BD": "Blue (NCS)",
            "#4666FF": "Neon blue",
            "#A4DDED": "Non Photo blue",
            "#002147": "Oxford Blue",
            "#273BE2": "Palatinate blue",
            "#87D3F8": "Pale azure",
            "#011F5B": "Penn Blue",
            "#CCCCFF": "Periwinkle",
            "#1C39BB": "Persian blue",
            "#000F89": "Phthalo blue",
            "#45B1E8": "Picton Blue",
            "#224C98": "Polynesian blue",
            "#9EB9D4": "Powder blue",
            "#003153": "Prussian blue",
            "#002387": "Resolution Blue",
            "#2454FF": "RISD Blue",
            "#4169E1": "Royal Blue (web color)",
            "#002366": "Royal blue (traditional)",
            "#76ABDF": "Ruddy Blue",
            "#0F52BA": "Sapphire",
            "#0076B6": "Honolulu Blue",
            "#4B61D1": "Savoy blue",
            "#5D89BA": "Silver Lake Blue",
            "#87CEEB": "Sky blue",
            "#1E2952": "Space cadet",
            "#4682B4": "Steel blue",
            "#0059CF": "Tang Blue",
            "#2D68C4": "True Blue",
            "#3E8EDE": "Tufts Blue",
            "#2774AE": "UCLA Blue",
            "#120A8F": "Ultramarine",
            "#4B92DB": "United Nations Blue",
            "#AFDBF5": "Uranian Blue",
            "#324AB2": "Violet Blue",
            "#7C9ED9": "Vista Blue",
            "#00356B": "Yale Blue",
            "#2E5090": "YInMn Blue",
            "#0014A8": "Zaffre",
            "#B284BE": "African violet",
            "#9966CC": "Amethyst",
            "#8A2BE2": "Blue Violet",
            "#9400D3": "Dark violet",
            "#BF00FF": "Electric purple",
            "#8F00FF": "Electric violet",
            "#563C5C": "English violet",
            "#6C3082": "Eminence",
            "#F2C1D1": "Fairy Tale",
            "#B53389": "Fandango",
            "#D473D4": "French mauve",
            "#8806CE": "French violet",
            "#FF00FF": "Fuchsia",
            "#6F2DA8": "Grape",
            "#DF73FF": "Heliotrope",
            "#5A4FCF": "Iris",
            "#5B3256": "Japanese violet",
            "#FFF0F5": "Lavender blush",
            "#B57EDC": "Lavender (floral)",
            "#E6E6FA": "Lavender (web)",
            "#C8A2C8": "Lilac",
            "#E0B0FF": "Mauve",
            "#8D029B": "Mauveine",
            "#997A8D": "Mountbatten pink",
            "#C54B8C": "Mulberry",
            "#8B004B": "Murrey",
            "#DA70D6": "Orchid",
            "#FAE6FA": "Pale purple",
            "#32127A": "Persian indigo",
            "#DF00FF": "Phlox",
            "#DBB2D1": "Pink lavender",
            "#DDA0DD": "Plum (web)",
            "#86608E": "Pomp and Power",
            "#CC8899": "Puce",
            "#9A4EAE": "Purpureus",
            "#7851A9": "Royal purple",
            "#C71585": "Red Violet",
            "#32174D": "Russian violet",
            "#6A5ACD": "Slate blue",
            "#CC33CC": "Steel pink",
            "#D8BFD8": "Thistle",
            "#9683EC": "Tropical indigo",
            "#66023C": "Tyrian purple",
            "#A020F0": "Veronica",
            "#8000FF": "Violet",
            "#C9A0DC": "Wisteria",
            "#E52B50": "Amaranth",
            "#FF91AF": "Baker-Miller Pink",
            "#8B008B": "Dark Magenta",
            "#FF1DCE": "Hot magenta",
            "#CA1F7B": "Magenta dye",
            "#FF0090": "Magenta (CMYK)",
            "#F653A6": "Magenta (Crayola)",
            "#D0417E": "Magenta (Pantone)",
            "#9F4576": "Magenta haze",
            "#8E4585": "Plum",
            "#FE4EDA": "Purple pizzazz",
            "#8E3A59": "Quinacridone magenta",
            "#E30B5C": "Raspberry",
            "#FF33CC": "Razzle dazzle rose",
            "#FF66CC": "Rose pink",
            "#AA98A9": "Rose quartz",
            "#FC0FC0": "Shocking pink",
            "#FF6FFF": "Shocking pink (Crayola)",
            "#CF71AF": "Sky magenta",
            "#CF3476": "Telemagenta",
            "#EE82EE": "Violet (web color)",
            "#F19CBB": "Amaranth pink",
            "#DE5D83": "Blush",
            "#FFA6C9": "Carnation pink",
            "#F1DDCF": "Champagne pink",
            "#FFB7C5": "Cherry blossom pink",
            "#A8516E": "China rose",
            "#F56FA1": "Cyclamen",
            "#FF1493": "Deep pink",
            "#D71868": "Dogwood rose",
            "#F64A8A": "French rose",
            "#C74375": "Fuchsia rose",
            "#F400A1": "Hollywood cerise",
            "#FF00CC": "Hot magenta",
            "#FF69B4": "Hot pink",
            "#FBAED2": "Lavender pink",
            "#E4007C": "Mexican pink",
            "#FFDAE9": "Mimi Pink",
            "#F2BDCD": "Orchid pink",
            "#EDCDC2": "Pale Dogwood",
            "#FFCBA4": "Peach",
            "#FE28A2": "Persian rose",
            "#F77FBE": "Persian pink",
            "#ED7A9B": "Rose Pompadour",
            "#E3256B": "Razzmatazz",
            "#B3446C": "Raspberry rose",
            "#F9429E": "Rose Bonbon",
            "#FFF5EE": "Seashell",
            "#FC89AC": "Tickle me pink",
            "#DE6FA1": "Thulian pink",
            "#EDEAE0": "Alabaster",
            "#F2F3F4": "Anti-flash white",
            "#FAEBD7": "Antique white",
            "#FEFEFA": "Baby powder",
            "#FFF8DC": "Cornsilk",
            "#EFDFBB": "Dutch white",
            "#F0EAD6": "Eggshell",
            "#FFFAF0": "Floral white",
            "#F8F8FF": "Ghost white",
            "#F4F0EC": "Isabelline",
            "#FAF0E6": "Linen",
            "#F8F4FF": "Magnolia",
            "#F5FFFA": "Mint cream",
            "#E9FFDB": "Nyanza",
            "#FDF5E6": "Old lace",
            "#F1E9D2": "Parchment",
            "#EAE0C8": "Pearl",
            "#E5E4E2": "Platinum",
            "#F7F7F7": "Seasalt",
            "#FFFAFA": "Snow",
            "#FFFFFF": "White",
            "#F5F5F5": "White smoke",
            "#DBD7D2": "Timberwolf",
            "#C0C0C0": "Silver",
            "#BEBFC5": "French gray",
            "#B2BEB5": "Ash gray",
            "#98817B": "Cinereous",
            "#91A3B0": "Cadet gray",
            "#8C92AC": "Cool gray",
            "#8B8589": "Taupe gray",
            "#2A3439": "Gunmetal",
            "#4D5D53": "Feldgrau",
            "#2F4F4F": "Dark slate gray",
            "#000000": "Black",
            "#4B3621": "Café noir",
            "#36454F": "Charcoal",
            "#555D50": "Ebony",
            "#1B1B1B": "Eerie black",
            "#1A1110": "Licorice",
            "#414A4C": "Outer space",
            "#010B13": "Rich black",
            "#100C08": "Smoky black",
        };

        return namedColors[hex] || "";
    };

    const handleCopyClick = () => {
        const textarea = document.createElement("textarea");
        textarea.value = `${selectedColor.rgba}\n${selectedColor.hex}`;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        alert("Value copied to clipboard!");
    };

    const handleClose = () => {
        setModalVisible(false);
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.keyCode === 27) {
                handleClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const getNeighboringPixels = (pixelColor) => {
        const numPixels = 3; // Number of neighboring pixels to include

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const { width, height } = imageData;
        const { offsetX, offsetY } = pixelColor;

        const colors = [];

        // Calculate the range of neighboring pixels
        const minX = Math.max(0, offsetX - numPixels);
        const maxX = Math.min(width - 1, offsetX + numPixels);
        const minY = Math.max(0, offsetY - numPixels);
        const maxY = Math.min(height - 1, offsetY + numPixels);

        // Extract the neighboring pixels' colors from the image data
        for (let y = minY; y <= maxY; y++) {
            const row = [];
            for (let x = minX; x <= maxX; x++) {
                const pixelIndex = (y * width + x) * 4;
                const r = imageData.data[pixelIndex];
                const g = imageData.data[pixelIndex + 1];
                const b = imageData.data[pixelIndex + 2];
                const a = imageData.data[pixelIndex + 3] / 255;
                const rgba = `rgba(${r}, ${g}, ${b}, ${a})`;
                row.push({ rgba });
            }
            colors.push(row);
        }

        return colors;
    };

    const infoBarStyle = {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "1rem",
        border: "1px solid #ccc",
        cursor: "pointer",
    };

    const selectedBarStyle = {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "1rem",
        border: "1px solid #ccc",
        cursor: "pointer",
    };

    const colorSquareStyle = {
        width: "50px",
        height: "50px",
        margin: "0.5rem",
    };

    const canvasStyle = {
        cursor: "crosshair",
    };

    const gridContainerStyle = {
        display: "grid",
        gridTemplateColumns:
            "repeat(1, 1fr)" /* Adjust the number of columns as needed */,
        gap: "0.5rem",
        border: "1px solid #333",
    };

    const pixelStyle = {
        width: "50px" /* Adjust the width to match the color square size */,
        height: "50px" /* Adjust the height to match the color square size */,
    };

    const invertColor = (color) => {
        const r = 255 - color.r;
        const g = 255 - color.g;
        const b = 255 - color.b;
        return `rgb(${r}, ${g}, ${b})`;
    };

    return (
        <div>
            <span
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "20px",
                    backgroundColor: "lightblue",
                    color: "darkblue",
                    fontSize: "1.0em",
                    borderRadius: "4px",
                    marginBottom: "20px", // Добавляем отступ между span и следующим дивом
                }}
            >
                {tip1}
            </span>

            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    border: "1px solid #333" /* , background: pixelColor.rgba */,
                }}
            >
                <canvas
                    width={windowSize.width * 0.9}
                    height={windowSize.height * 0.9}
                    ref={canvasRef}
                    onMouseMove={handleMouseMove}
                    onClick={handleClick}
                    style={canvasStyle}
                    onMouseLeave={handleMouseLeave}
                    onMouseEnter={handleMouseEnter}
                    /* onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd} */
                />
                {isCursorInsideCanvas && (
                    <div
                        style={{
                            position: "fixed",
                            left: "0",
                            top: "30%",
                        }}
                    >
                        <div
                            style={{
                                transform: "translateY(-50%)",
                                width: "50px",
                                height: "50px",
                                backgroundColor: pixelColor.rgba,
                                border: "1px solid #333",
                                borderRadius: "100%",
                            }}
                        ></div>
                        <table
                            className="neighboring-pixels"
                            style={gridContainerStyle}
                        >
                            <tbody>
                                {neighboringColors.map((colorRow, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {colorRow.map((color, cellIndex) => (
                                            <td
                                                key={cellIndex}
                                                className="neighboring-pixel"
                                                style={{
                                                    backgroundColor: color.rgba,
                                                    width: "20px",
                                                    height: "20px",
                                                    border:
                                                        rowIndex ===
                                                            Math.floor(
                                                                neighboringColors.length /
                                                                    2
                                                            ) &&
                                                        cellIndex ===
                                                            Math.floor(
                                                                colorRow.length /
                                                                    2
                                                            )
                                                            ? "2px solid white" // Инвертированный цвет границы для центрального пикселя
                                                            : "none", // Нет границы для остальных пикселей
                                                }}
                                            ></td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {modalVisible && (
                    <div
                        style={{
                            position: "fixed",
                            bottom: 0,
                            left: 0,
                            width: "100%",
                            height: maxHeight,
                            backgroundColor: "white",
                            padding: "2vw",
                            borderRadius: "4px",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                            overflow: "hidden",
                            transition: "height 0.3s ease",
                            zIndex: 9999,
                        }}
                        className={/* isPopupVisible ? 'visible' : '' */ "true"}
                    >
                        <div
                            style={{
                                maxHeight: "400px",
                                overflowX: "auto",
                                overflowY: "scroll",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <div style={{ flex: "1 1 auto" }}>
                                {/* Ваш контент здесь */}
                                <ColorInfo
                                    selectedColor={selectedColor}
                                    attributes={props.attributes}
                                />
                            </div>
                        </div>
                        <button
                            style={{
                                position: "absolute",
                                top: "10px",
                                right: "20px",
                                width: "30px",
                                height: "30px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "24px",
                                color: "gray",
                                margin: "10px",
                            }}
                            onClick={handleClose}
                        >
                            <span style={{ fontSize: "10px" }}>(ESC)</span>{" "}
                            &#x2715;
                        </button>
                    </div>
                )}
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "20px",
                    margin: "20px" /* alignItems: 'center', */ /* height: '100vh' */,
                }}
            >
                <div id="selected" ref={selectedRef}></div>
                <div
                    style={{
                        width: 50 + "%",
                        padding: "20px",
                        border: "1px solid #333",
                    }}
                >
                    <div
                        style={{
                            width: "20px",
                            height: "20px",
                            background: pixelColor.rgba,
                        }}
                    ></div>

                    <h3>{titleCurrentValue}:</h3>
                    <p
                        className="px-4 py-2 mb-2"
                        style={{ wordBreak: "break-word" }}
                    >
                        RGBA: {pixelColor.rgba}
                    </p>
                    <p
                        className="px-4 py-2 mb-2"
                        style={{ wordBreak: "break-word" }}
                    >
                        HEX: {pixelColor.hex}
                    </p>
                    <p
                        className="px-4 py-2 mb-2"
                        style={{ wordBreak: "break-word" }}
                    >
                        HSL: {pixelColor.hsl}
                    </p>
                    <p
                        className="px-4 py-2 mb-2"
                        style={{ wordBreak: "break-word" }}
                    >
                        CMYK: {pixelColor.cmyk}
                    </p>
                    <p
                        className="px-4 py-2 mb-2"
                        style={{ wordBreak: "break-word" }}
                    >
                        HSV: {pixelColor.hsv}
                    </p>
                    <p
                        className="px-4 py-2 mb-2"
                        style={{ wordBreak: "break-word" }}
                    >
                        LAB: {pixelColor.lab}
                    </p>
                    <p
                        className="px-4 py-2 mb-2"
                        style={{ wordBreak: "break-word" }}
                    >
                        Name: {pixelColor.name}
                    </p>
                    <p
                        className="px-4 py-2 mb-2"
                        style={{ wordBreak: "break-word" }}
                    >
                        RGBA (Alpha): {pixelColor.rgbaA}
                    </p>
                    <p
                        className="px-4 py-2 mb-2"
                        style={{ wordBreak: "break-word" }}
                    >
                        HSLA: {pixelColor.hsla}
                    </p>
                    <p
                        className="px-4 py-2 mb-2"
                        style={{ wordBreak: "break-word" }}
                    >
                        XYZ: {pixelColor.xyz}
                    </p>
                    <p
                        className="px-4 py-2 mb-2"
                        style={{ wordBreak: "break-word" }}
                    >
                        LCH: {pixelColor.lch}
                    </p>
                </div>

                <ColorInfo
                    selectedColor={selectedColor}
                    attributes={props.attributes}
                />
            </div>
        </div>
    );
};

export default PixelColorViewer;
